import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  COLORS,
  STAGE_COLORS,
  ChartCard,
  KpiCard,
  Notes,
  convertFromUsd,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatPeriodLabel,
  tooltipProps,
} from '../components/ChartKit';

/** FR-M13-001 Executive dashboard */
const OverviewTab = ({ data, dashboard, periodLabel, currency = 'USD' }) => {
  const k = data.kpis;
  const trend = data.trend.points.map((p) => ({
    ...p,
    label: formatPeriodLabel(p.period, data.trend.granularity),
  }));
  const hasTrend = trend.some((p) => p.newLeads || p.won || p.lost || p.meetings);
  const stages = data.stageSnapshot.filter((s) => s.count > 0);
  const sources = data.segments.bySource;

  // All money is summed in USD on the server; here we only convert for display.
  // The currency picker lives in the module toolbar (next to Export CSV).
  const rates = data.fx?.rates || { USD: 1 };
  const shown = (usd) => formatCurrency(convertFromUsd(usd, currency, rates), currency);
  const conversionNote =
    currency === 'USD'
      ? 'Money totals are calculated in USD.'
      : `Money totals are calculated in USD and converted to ${currency}.`;
  const rateNote = data.fx?.date
    ? `Exchange rates as of ${data.fx.date}${data.fx.source === 'fallback' ? ' (built-in rates; live rates unavailable)' : ''}.`
    : 'Using built-in exchange rates.';
  const unconvertedNote = data.fx?.unconverted?.length
    ? `Amounts in ${data.fx.unconverted.join(', ')} could not be converted and are not included in the totals.`
    : null;

  return (
    <div className="analytics-tab-body">
      <div className="analytics-kpis">
        <KpiCard
          icon="fa-user-plus"
          label="New leads"
          value={formatNumber(k.newLeads)}
          hint={periodLabel}
        />
        <KpiCard
          icon="fa-user-check"
          label="Qualified leads"
          value={formatNumber(k.qualifiedLeads)}
          hint={`${formatPercent(k.qualificationRate)} of new leads`}
          tone="violet"
        />
        <KpiCard
          icon="fa-calendar-check"
          label="Meetings held"
          value={formatNumber(k.meetingsHeld)}
          hint={`${formatNumber(k.upcomingMeetings)} upcoming`}
          tone="cyan"
        />
        <KpiCard
          icon="fa-briefcase"
          label="Open opportunities"
          value={formatNumber(k.openOpportunities)}
          hint="In Proposal or Negotiation"
          tone="amber"
        />
        <KpiCard
          icon="fa-sack-dollar"
          label="Open pipeline value"
          value={shown(k.pipelineValueUsd)}
          hint={`${formatNumber(k.openLeads)} open leads`}
        />
        <KpiCard
          icon="fa-trophy"
          label="Revenue won"
          value={shown(k.revenueWonUsd)}
          hint={`${formatNumber(k.wonDeals)} deals won · ${formatNumber(k.lostDeals)} lost`}
          tone="green"
        />
        <KpiCard
          icon="fa-percent"
          label="Win rate"
          value={formatPercent(k.winRate)}
          hint="Won ÷ (won + lost)"
          tone="green"
        />
        <KpiCard
          icon="fa-coins"
          label="Credits used"
          value={formatNumber(k.creditsUsed)}
          hint={dashboard.coverage.credits}
          tone="slate"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          wide
          title="Activity over time"
          subtitle={`New leads, deals won and lost, and meetings held · ${periodLabel}`}
          empty={!hasTrend}
          emptyText="No lead or meeting activity in this period yet."
          table={{
            columns: ['Period', 'New leads', 'Won', 'Lost', 'Meetings'],
            rows: trend.map((p) => [p.label, p.newLeads, p.won, p.lost, p.meetings]),
          }}
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip {...tooltipProps} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="newLeads"
                name="New leads"
                stroke="#2563eb"
                fill="url(#gLeads)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="meetings"
                name="Meetings"
                stroke="#0891b2"
                fill="none"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="won"
                name="Won"
                stroke="#16a34a"
                fill="none"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="lost"
                name="Lost"
                stroke="#dc2626"
                fill="none"
                strokeWidth={2}
                strokeDasharray="4 3"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Pipeline by stage"
          subtitle="All leads you can access, right now"
          empty={!stages.length}
          table={{
            columns: ['Stage', 'Leads'],
            rows: data.stageSnapshot.map((s) => [s.stage, s.count]),
          }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.stageSnapshot} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip {...tooltipProps} />
              <Bar dataKey="count" name="Leads" radius={[4, 4, 0, 0]}>
                {data.stageSnapshot.map((s) => (
                  <Cell key={s.stage} fill={STAGE_COLORS[s.stage] || '#2563eb'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="New leads by source"
          subtitle={periodLabel}
          empty={!sources.length}
          table={{ columns: ['Source', 'Leads'], rows: sources.map((s) => [s.label, s.count]) }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={sources}
                dataKey="count"
                nameKey="label"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
              >
                {sources.map((s, i) => (
                  <Cell key={s.label} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip {...tooltipProps} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <SegmentList title="New leads by industry" items={data.segments.byIndustry} />
        <SegmentList title="New leads by country" items={data.segments.byCountry} />
      </div>

      <Notes
        items={[
          ...(data.notes || []),
          conversionNote,
          rateNote,
          unconvertedNote,
          coverageNote(dashboard),
        ].filter(Boolean)}
      />
    </div>
  );
};

const coverageNote = (dashboard) =>
  `Figures cover ${dashboard.coverage.leads}, ${dashboard.coverage.meetings} and ${dashboard.coverage.credits}.${
    dashboard.coverage.leadsCapped ? ' Only the most recent 5,000 leads are included.' : ''
  }`;

const SegmentList = ({ title, items }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <ChartCard title={title} empty={!items.length}>
      <ul className="analytics-bars">
        {items.map((i) => (
          <li key={i.label}>
            <span className="analytics-bars-label">{i.label}</span>
            <span className="analytics-bars-track" aria-hidden="true">
              <span
                className="analytics-bars-fill"
                style={{ width: `${(i.count / max) * 100}%` }}
              />
            </span>
            <span className="analytics-bars-value">{formatNumber(i.count)}</span>
          </li>
        ))}
      </ul>
    </ChartCard>
  );
};

export { SegmentList };
export default OverviewTab;
